import { Fragment, useState } from 'react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useAuth } from '@/hooks/useAuth'
import { useCurrentCrewMember } from '@/hooks/useCurrentCrewMember'
import { useCrewGetById } from '@/lib/hooks'
import { useDocumentGetByCrew } from '@/lib/hooks/document'
import { useCompanyListMine } from '@/lib/hooks/company'
import { useIsAdmin } from '@/hooks/useIsCompanyAdmin'
import { useAppVersion, formatAppVersion } from '@/hooks/useAppVersion'
import {
  Settings,
  Building2,
  HeartHandshake,
  LogOut,
  ChevronRight,
  Plus,
  FileText,
  FileBadge2,
  IdCard,
  IdCardLanyard,
  File,
  TriangleAlert,
  CircleAlert,
  CircleCheck,
} from '@/components/icons'
import { ProfileAvatarWithBadge } from '@/components/ProfileAvatarWithBadge'
import { TopBarBackButton } from '@/components/topbar'
import { Card } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { AppLayout } from '@/components/AppLayout'
import { formatCrewRole, cn } from '@/lib/utils'
import { CrewFlightStats } from '@/components/CrewFlightStats'
import { DesktopPage } from '@/components/desktop/DesktopPage'
import { DesktopPageHeader } from '@/components/desktop/DesktopPageHeader'
import { useIsDesktop } from '@/components/ui/use-mobile'
import { DocumentSubType, documentSubTypeLabels, isPilotRole } from '@@api/shared'
import { ViewDocumentDrawer } from '@/components/documents/ViewDocumentDrawer'
import { DocumentFormDrawer } from '@/components/documents/DocumentFormDrawer'
import type { FC, SVGProps } from 'react'
import dayjs from 'dayjs'

export const Route = createFileRoute('/_auth/profile/')({
  component: ProfileRoute,
})

type ExpiryStatus = 'valid' | 'expiring' | 'expired'

function getDocumentIcon(type: DocumentSubType): FC<SVGProps<SVGSVGElement>> {
  switch (type) {
    case DocumentSubType.CrewID:
      return IdCardLanyard
    case DocumentSubType.Passport:
      return IdCard
    case DocumentSubType.Visa:
      return FileBadge2
    default:
      return FileText
  }
}

function getDocumentExpiryStatus(expiryDate: Date | string): ExpiryStatus {
  const now = dayjs()
  const expiry = dayjs(expiryDate)
  if (expiry.isBefore(now)) return 'expired'
  if (expiry.isBefore(now.add(2, 'month'))) return 'expiring'
  return 'valid'
}

function ExpiryStatusBadge({ status }: { status?: ExpiryStatus }) {
  if (status === 'expired') {
    return (
      <span className="flex items-center justify-center size-6 rounded-full bg-background-negative-subtle">
        <TriangleAlert className="size-4 text-content-negative-bold" />
      </span>
    )
  }
  if (status === 'expiring') {
    return (
      <span className="flex items-center justify-center size-6 rounded-full bg-background-notice-subtle">
        <CircleAlert className="size-4 text-background-notice-bold" />
      </span>
    )
  }
  return (
    <span className="flex items-center justify-center size-6 rounded-full bg-background-positive-subtle">
      <CircleCheck className="size-4 text-content-positive-bold" />
    </span>
  )
}

function YesNoBadge({ value }: { value: boolean }) {
  return (
    <Badge
      variant="outline"
      className={`rounded-lg text-xs font-semibold ${
        value ? 'text-content-positive-bold' : 'text-content-primary-subtle'
      }`}
    >
      {value ? 'Yes' : 'No'}
    </Badge>
  )
}

function ProfileRoute() {
  const navigate = useNavigate()
  const isDesktop = useIsDesktop()
  const appVersionLabel = formatAppVersion(useAppVersion())
  const { signOut } = useAuth()
  const { data: currentUser, isPending: userLoading } = useCurrentCrewMember()
  const { data: crewDetail, isPending: statsPending } = useCrewGetById(
    currentUser?.id ?? ''
  )
  const statsLoading = userLoading || statsPending
  const { isAdmin } = useIsAdmin()
  const { data: crewDocuments, isPending: documentsPending } =
    useDocumentGetByCrew(currentUser?.id ?? '')
  const { data: myCompanies } = useCompanyListMine()
  const hasMultipleCompanies = (myCompanies?.length ?? 0) > 1
  const documentsLoading = userLoading || documentsPending

  const [selectedDocumentId, setSelectedDocumentId] = useState<string | null>(null)
  // Separate open flag; the id is kept on close so the view drawer can play
  // its exit animation (the next tap overwrites it).
  const [isViewDrawerOpen, setIsViewDrawerOpen] = useState(false)
  const [isAddDrawerOpen, setIsAddDrawerOpen] = useState(false)

  const docCount = crewDocuments?.length ?? 0
  // Pilot License row only applies to pilots
  const isPilot = isPilotRole(currentUser?.crewRole)

  const getUserInitials = () => {
    if (!currentUser) return 'U'
    if (currentUser.firstName && currentUser.lastName) {
      return `${currentUser.firstName.charAt(0)}${currentUser.lastName.charAt(0)}`.toUpperCase()
    }
    if (currentUser.name) {
      const nameParts = currentUser.name.trim().split(' ')
      if (nameParts.length >= 2) {
        return `${nameParts[0].charAt(0)}${nameParts[nameParts.length - 1].charAt(0)}`.toUpperCase()
      }
      return nameParts[0].charAt(0).toUpperCase()
    }
    return 'U'
  }

  const getUserFirstName = () => {
    if (!currentUser) return 'User'
    if (currentUser.firstName) return currentUser.firstName
    if (currentUser.name) {
      const nameParts = currentUser.name.trim().split(' ')
      return nameParts[0]
    }
    return 'User'
  }

  const handleLogout = async () => {
    await signOut()
    navigate({ to: '/' })
  }

  const body = (
    <div
      className={cn(
        'space-y-3',
        !isDesktop && 'pb-[calc(var(--safe-bottom)+32px)]',
      )}
    >
      {/* Profile Card with Stats */}
        <Card className="flex flex-row gap-8 items-center px-6 py-4">
          <div className="flex-1 flex items-center justify-center">
            <div className="flex flex-col gap-3 items-center justify-center">
              {userLoading ? (
                <>
                  <Skeleton className="size-[72px] rounded-full" />
                  <Skeleton className="h-7 w-20" />
                </>
              ) : (
                <>
                  <ProfileAvatarWithBadge
                    fallbackText={getUserInitials()}
                    className="size-[72px] text-xl"
                    photoS3Key={currentUser?.photoS3Key}
                    avatarColor={currentUser?.avatarColor}
                    isAdmin={isAdmin}
                    useCurrentUser={false}
                  />
                  <p className="text-lg font-semibold text-content-primary-bold">
                    {getUserFirstName()}
                  </p>
                  {currentUser?.company?.name && (
                    <p className="text-sm font-medium text-content-primary-subtle">
                      {currentUser.company.name}
                    </p>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Right - Flight Stats (from submitted flight logs, same as crew page) */}
          <CrewFlightStats
            crew={{
              crewRole: currentUser?.crewRole,
              flightStats: crewDetail?.flightStats,
            }}
            loading={statsLoading}
          />
        </Card>

        {/* Account Info Section */}
        <div className="flex flex-col gap-3 pt-6">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[1.5px]">
            <span className="text-content-primary-subtle-mid">Account</span>
          </div>
          <Card className="flex flex-col gap-2 p-4">
            {userLoading ? (
              Array.from({ length: 5 }).map((_, index) => (
                <Fragment key={index}>
                  {index > 0 && <hr className="border-border-L1" />}
                  <div className="flex items-center justify-between">
                    <Skeleton className="h-5 w-24" />
                    <Skeleton className="h-5 w-32" />
                  </div>
                </Fragment>
              ))
            ) : (
              <>
                <div className="flex items-center justify-between gap-4">
                  <p className="text-sm font-medium text-content-primary-subtle shrink-0">
                    Email
                  </p>
                  <p className="text-sm font-semibold text-content-primary-bold truncate">
                    {currentUser?.email}
                  </p>
                </div>
                <hr className="border-border-L1" />
                <div className="flex items-center justify-between gap-4">
                  <p className="text-sm font-medium text-content-primary-subtle shrink-0">
                    Role
                  </p>
                  <p className="text-sm font-semibold text-content-primary-bold truncate">
                    {formatCrewRole(currentUser?.crewRole) || '-'}
                  </p>
                </div>
                {isPilot && (
                  <>
                    <hr className="border-border-L1" />
                    <div className="flex items-center justify-between gap-4">
                      <p className="text-sm font-medium text-content-primary-subtle shrink-0">
                        Pilot License No.
                      </p>
                      <p className="text-sm font-semibold text-content-primary-bold truncate">
                        {currentUser?.pilotLicenseNumber || '-'}
                      </p>
                    </div>
                  </>
                )}
                <hr className="border-border-L1" />
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium text-content-primary-subtle">
                    Admin
                  </p>
                  <YesNoBadge value={currentUser?.isAdmin ?? false} />
                </div>
                <hr className="border-border-L1" />
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium text-content-primary-subtle">
                    Owner
                  </p>
                  <YesNoBadge value={currentUser?.isOwner ?? false} />
                </div>
                <hr className="border-border-L1" />
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium text-content-primary-subtle">
                    Included in schedule
                  </p>
                  <YesNoBadge value={currentUser?.includeInSchedule !== false} />
                </div>
              </>
            )}
          </Card>
        </div>

        {/* My Documents Section */}
        <div className="flex flex-col gap-3 pt-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[1.5px]">
              <span className="text-content-primary-subtle-mid">My Documents</span>
              {!documentsLoading && docCount > 0 && (
                <span className="text-content-brand-bold">{docCount}</span>
              )}
            </div>
            <button
              type="button"
              onClick={() => setIsAddDrawerOpen(true)}
              className="flex items-center justify-center size-8 rounded-full"
              aria-label="Add document"
            >
              <Plus className="size-5 text-content-primary-bold" />
            </button>
          </div>

          {documentsLoading ? (
            <Card className="flex flex-col gap-2 p-4">
              {Array.from({ length: 3 }).map((_, index) => (
                <Fragment key={index}>
                  {index > 0 && <hr className="border-border-L1" />}
                  <div className="flex items-center justify-between w-full">
                    <div className="flex items-center gap-4">
                      <Skeleton className="size-6 rounded-md shrink-0" />
                      <div className="flex flex-col gap-2 items-start">
                        <Skeleton className="h-6 w-32" />
                        <div className="flex gap-3">
                          <Skeleton className="h-5 w-20 rounded-lg" />
                          <Skeleton className="h-5 w-28 rounded-lg" />
                        </div>
                      </div>
                    </div>
                    <Skeleton className="size-6 rounded-full" />
                  </div>
                </Fragment>
              ))}
            </Card>
          ) : docCount > 0 ? (
            <Card className="flex flex-col gap-2 p-4">
              {crewDocuments!.map((doc, index) => {
                const DocumentIcon = getDocumentIcon(doc.subType)
                const expiryStatus = doc.expiryDate
                  ? getDocumentExpiryStatus(doc.expiryDate)
                  : undefined
                return (
                  <Fragment key={doc.id}>
                    {index > 0 && <hr className="border-border-L1" />}
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedDocumentId(doc.id)
                        setIsViewDrawerOpen(true)
                      }}
                      className="flex items-center justify-between w-full"
                    >
                      <div className="flex items-center gap-4">
                        <DocumentIcon className="size-6 text-content-brand-bold shrink-0" />
                        <div className="flex flex-col gap-2 items-start">
                          <p className="text-base font-semibold text-content-primary-bold tracking-[-0.5px] leading-6">
                            {doc.title}
                          </p>
                          <div className="flex flex-wrap gap-3">
                            <Badge
                              variant="outline"
                              className="rounded-lg text-xs font-semibold text-content-primary-subtle"
                            >
                              {documentSubTypeLabels[doc.subType] || doc.subType}
                            </Badge>
                            {doc.expiryDate && (
                              <Badge
                                variant="outline"
                                className="rounded-lg text-xs font-semibold text-content-primary-subtle"
                              >
                                Expires{' '}
                                {new Date(doc.expiryDate).toLocaleDateString(
                                  'en-US',
                                  {
                                    month: 'short',
                                    day: 'numeric',
                                    year: 'numeric',
                                  }
                                )}
                              </Badge>
                            )}
                            <Badge
                              variant="outline"
                              className="rounded-lg text-xs font-semibold text-content-primary-subtle"
                            >
                              {doc.isPublic ? 'Public' : 'Private'}
                            </Badge>
                          </div>
                        </div>
                      </div>
                      <ExpiryStatusBadge status={expiryStatus} />
                    </button>
                  </Fragment>
                )
              })}
            </Card>
          ) : (
            <Card className="flex flex-col items-center justify-center p-6 gap-2">
              <span className="flex items-center justify-center rounded-lg bg-background-brand-secondary-subtle p-2">
                <File className="size-4 text-content-brand-bold" />
              </span>
              <p className="text-sm font-medium text-content-primary-subtle">
                No documents yet
              </p>
            </Card>
          )}
        </div>

        {/* Menu Items */}
        <div className="flex flex-col gap-3 pt-6">
          <Card
            interactive
            className="flex items-center gap-2 px-4 py-4 h-[54px] cursor-pointer"
            onClick={() => navigate({ to: '/profile/settings' })}
          >
            <Settings className="size-6 text-content-primary-bold" />
            <span className="flex-1 text-sm font-semibold text-content-primary-bold">
              Account Settings
            </span>
            <ChevronRight className="size-6 text-content-primary-bold" />
          </Card>

          {hasMultipleCompanies && (
            <Card
              interactive
              className="flex items-center gap-2 px-4 py-4 h-[54px] cursor-pointer"
              onClick={() => navigate({ to: '/profile/company' })}
            >
              <Building2 className="size-6 text-content-primary-bold" />
              <span className="flex-1 text-sm font-semibold text-content-primary-bold">
                Change Company
              </span>
              <ChevronRight className="size-6 text-content-primary-bold" />
            </Card>
          )}

          <Card
            interactive
            className="flex items-center gap-2 px-4 py-4 h-[54px] cursor-pointer"
            onClick={() => navigate({ to: '/profile/help-support' })}
          >
            <HeartHandshake className="size-6 text-content-primary-bold" />
            <span className="flex-1 text-sm font-semibold text-content-primary-bold">
              Help & Support
            </span>
            <ChevronRight className="size-6 text-content-primary-bold" />
          </Card>

          <Card
            interactive
            className="flex items-center gap-2 px-4 py-4 h-[54px] cursor-pointer"
            onClick={handleLogout}
          >
            <LogOut className="size-6 text-content-negative-bold" />
            <span className="flex-1 text-sm font-semibold text-content-negative-bold">
              Logout
            </span>
          </Card>
        </div>

        {appVersionLabel && (
          <p className="pt-2 text-center text-xs text-content-primary-subtle">
            {appVersionLabel}
          </p>
        )}
      </div>
  )

  const drawers = (
    <>
      <ViewDocumentDrawer
        isOpen={isViewDrawerOpen}
        onClose={() => setIsViewDrawerOpen(false)}
        documentId={selectedDocumentId}
        canManage
      />

      <DocumentFormDrawer
        isOpen={isAddDrawerOpen}
        onClose={() => setIsAddDrawerOpen(false)}
        owner={{ type: 'crew', crewId: currentUser?.id ?? '' }}
      />
    </>
  )

  if (isDesktop) {
    return (
      <>
        <DesktopPage>
          <DesktopPageHeader title="My Profile" />
          {body}
        </DesktopPage>
        {drawers}
      </>
    )
  }

  return (
    <AppLayout
      topBar={<TopBarBackButton title="My Profile" />}
      className="pt-6"
    >
      {body}
      {drawers}
    </AppLayout>
  )
}
